<script setup lang="ts">
import { useGoogleAnalyticsPageView } from '~/composables/google-analytics'

const { $seoManager } = useNuxtApp()

const seo = $seoManager.generateSeoInfo({
  title: 'Privacy',
})
useHead(seo)
useGoogleAnalyticsPageView(seo.title)
</script>

<template>
  <SimplePageContent>
    <SimplePageTitle title="BENCHMARK MINERALS PRIVACY NOTICE" />
    <div
      class="prose underline-offset-2 prose-p:text-s-900 prose-a:break-all prose-a:text-s-700 prose-a:decoration-s-400 prose-a:decoration-dotted hover:prose-a:decoration-p-900 prose-li:text-s-900 md:prose-a:break-normal"
    >
      <ol>
        <li>
          <p style="font-weight: bold"
            >INTRODUCTION AND IMPORTANT INFORMATION</p
          >
          <ol type="a">
            <li>
              <div>
                <p style="font-weight: bold">Purpose of this Privacy Notice</p>
                <p>
                  This privacy notice (together with our terms of use
                  <a href="https://benchmarkminerals.com/terms"
                    >https://benchmarkminerals.com/terms</a
                  >
                  and any other documents referred to in it) ("Notice") sets out
                  information as to how Benchmark Mineral Intelligence Limited
                  of Suite 51, Pure Offices, Kembrey Park, Swindon, SN2 8BW
                  (referred to as "Benchmark" "we", "us" or "our" in this
                  Privacy Notice) processes any information relating to an
                  identified or identifiable natural person (“Personal Data”)
                  that we collect from you, or that you provide to us. Please
                  read the following carefully to understand our practices
                  regarding your Personal Data and how we will treat it.
                </p>
              </div>
            </li>
            <li>
              <div>
                <p style="font-weight: bold">Data Controller</p>
                <p
                  >Benchmark is the controller and responsible for your Personal
                  Data. Rho Publishing and Consulting Ltd (trading as Rho
                  Motion) operates as a brand of Benchmark. All marketing
                  communications are managed centrally by Benchmark Mineral
                  Intelligence Limited, which acts as the sole data
                  controller.</p
                >
              </div>
            </li>
            <li>
              <div>
                <p style="font-weight: bold">Group Companies</p>
                <p>
                  Benchmark Mineral Intelligence Limited is part of Benchmark
                  Intelligence Group Limited. Other group companies include Rho
                  Publishing and Consulting Ltd (trading as Rho Motion). While
                  each company operates under its own brand, we may share your
                  Personal Data with other companies within the group for
                  internal administrative purposes, and (subject to your
                  marketing preferences and applicable law) to send you
                  information about products, services, events and insights
                  offered by them which may be of interest to you.
                </p>
              </div>
            </li>
            <li>
              <div>
                <p style="font-weight: bold">Contact Details</p>
                <p>
                  Questions, comments and requests regarding this Notice are
                  welcomed and should be addressed to
                  <a href="mailto:amiller@benchmarkminerals"
                    >amiller@benchmarkminerals</a
                  >.com or sent by post to the above mentioned address.
                </p>
              </div>
            </li>
            <li>
              <div>
                <p style="font-weight: bold">Changes to this Notice</p>
                <p>
                  Any changes we make to this Notice in the future will be
                  posted on this page and, where appropriate, notified to you by
                  e-mail. Please check back frequently to see any updates or
                  changes to this Notice. It is important that the information
                  we hold about you is accurate and current. Please keep us
                  informed if your Personal Data changes during your
                  relationship with us.
                </p>
              </div>
            </li>
            <li>
              <div>
                <p style="font-weight: bold">Third Party Links</p>
                <p>
                  Our site may, from time to time, contain links to and from the
                  websites of our partner networks, advertisers and affiliates.
                  If you follow a link to any of these websites, please note
                  that these websites have their own privacy policies and that
                  we do not accept any responsibility or liability for these
                  policies. Please check these policies before you submit any
                  Personal Data to these websites.
                </p>
              </div>
            </li>
          </ol>
        </li>
        <li>
          <p style="font-weight: bold"
            >WHAT PERSONAL DATA DO WE COLLECT AND WHY?</p
          >
          <ol type="a">
            <li>
              <div>
                <p style="font-weight: bold"
                  >The Types of Personal Data we Process</p
                >
                <ol type="i">
                  <li
                    >Contact details, including your name, billing address,
                    delivery address, e-mail address and phone number ("Contact
                    Data")</li
                  >
                  <li
                    >Identification details, including your name, personal
                    description and photograph, date of birth, gender, marital
                    status, title ("Identity Data")</li
                  >
                  <li
                    >Financial details, including your financial and payment
                    card details ("Financial Data")</li
                  >
                  <li
                    >Technical details in relation to how you use
                    <a href="http://www.benchmarkminerals.com" target="_blank"
                      >http://www.benchmarkminerals.com</a
                    >
                    (“Our Site”) including your login information, browser type
                    and version, time zone setting, browser plug-in types and
                    versions, operating system and platform ("Technical
                    Data")</li
                  >
                  <li
                    >Information about your visit(s) to Our Site, including the
                    full Uniform Resource Locators (URL), clickstream to,
                    through and from Our Site (including date and time),
                    products you viewed or searched for, page response times,
                    download errors, length of visits to certain pages, page
                    interaction information (such as scrolling, clicks, and
                    mouse-overs), methods used to browse away from the page
                    ("Usage Data")</li
                  >
                  <li
                    >Your interests, preferences, feedback, survey responses,
                    payments to and from you, details of products/services you
                    have purchased, and event or webinar registration
                    information (including joint events co-hosted with third
                    parties). ("Transaction Data")</li
                  >
                  <li
                    >Your preferences in receiving marketing from us and third
                    parties and your communication preferences ("Marketing
                    Data")</li
                  >
                </ol>
              </div>
            </li>
            <li>
              <div>
                <p style="font-weight: bold"
                  >Where we Collect Personal Data From</p
                >
                <p>We may collect your Personal Data from you when you:</p>
                <ol type="i">
                  <li
                    >Use Our Site, including when you: fill in forms on Our
                    Site, register to use Our Site, subscribe to our
                    subscription service which can include news, pricing, data,
                    analysis and consultancy in relation to batteries, EV’s,
                    energy storage etc. (the “Service”), search for a product,
                    place an order on Our Site, participate in discussion boards
                    or other social media functions on Our Site, enter a
                    competition, promotion or survey, and when you report a
                    problem with Our Site; or</li
                  >
                  <li>Correspond with us by phone, e-mail or otherwise.</li>
                </ol>
                <p
                  >We may also collect your Personal Data from third party
                  sources. We are working closely with third parties (including,
                  for example, business partners, sub-contractors in technical,
                  payment and delivery services, advertising networks, analytics
                  providers, search information providers, credit reference
                  agencies). We will notify you when we receive information
                  about you from them and the purposes for which we intend to
                  use that information.</p
                >
              </div>
            </li>
            <li>
              <div>
                <p style="font-weight: bold"
                  >We use information held about you in the following ways:</p
                >
                <p
                  >We will only use your Personal Data when the law allows us
                  to. Most commonly, we will use your Personal Data in the
                  following circumstances:</p
                >
                <ul>
                  <li
                    >Where we need to perform the contract we are about to enter
                    into or have entered into with you.</li
                  >
                  <li
                    >Where it is necessary for our legitimate interests (or
                    those of a third party) and your interests and fundamental
                    rights do not override those interests.</li
                  >
                  <li>Where we need to comply with a legal obligation.</li>
                </ul>
                <p
                  >We have set out below, in a table format, a description of
                  all the ways we plan to use your Personal Data and which of
                  the legal bases we rely on to do so. Note that we may process
                  your Personal Data for more than one lawful group.</p
                >
              </div>
              <div class="overflow-auto">
                <table style="width: 100%">
                  <colgroup>
                    <col span="1" style="width: 30%" />
                    <col span="1" style="width: 30%" />
                    <col span="1" style="width: 40%" />
                  </colgroup>
                  <thead>
                    <tr>
                      <th>Purpose/Activity</th>
                      <th>Type of Data</th>
                      <th>Lawful basis for processing</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>To register you as a new customer</td>
                      <td>
                        <p>(a) Identity Data</p>
                        <p>(b) Contact Data</p>
                      </td>
                      <td>Performance of a contract with you</td>
                    </tr>
                    <tr>
                      <td
                        >To provide you with information, products and services
                        that you request from us</td
                      >
                      <td>
                        <p>(a) Identity Data</p>
                        <p>(b) Marketing Data</p>
                      </td>
                      <td>
                        <p>(a) Performance of a contract with you</p>
                        <p>(b) Necessary for our legitimate interests</p>
                      </td>
                    </tr>
                    <tr>
                      <td
                        >To process and deliver your order including managing
                        payments and collecting and recovering money owed to
                        us</td
                      >
                      <td>
                        <p>(a) Identity Data</p>
                        <p>(b) Contact Data</p>
                        <p>(c) Financial Data</p>
                        <p>(d) Transaction Data</p>
                        <p>(e) Marketing Data</p>
                      </td>
                      <td>
                        <p>(a) Performance of a contract with you</p>
                        <p
                          >(b) Necessary for our legitimate interests (to
                          recover debts due to us)</p
                        >
                      </td>
                    </tr>
                    <tr>
                      <td
                        >To manage our relationship with you, including: to
                        notify you about changes to our service, updating you
                        about this Notice and asking you to leave a review of
                        take a survey</td
                      >
                      <td>
                        <p>(a) Identity Data</p>
                        <p>(b) Contact Data</p>
                        <p>(c) Other</p>
                      </td>
                      <td>
                        <p>(a) Performance of a contract with you</p>
                        <p>(b) Necessary to comply with a legal obligation</p>
                        <p
                          >(c) Necessary for our legitimate interests (to keep
                          our records updated and to study how customers use our
                          products/services)</p
                        >
                      </td>
                    </tr>
                    <tr>
                      <td
                        >To provide you with information about other goods and
                        services we offer that are similar to those that you
                        have already purchased or enquired about</td
                      >
                      <td>
                        <p>(a) Identity Data</p>
                        <p>(b) Contact Data</p>
                        <p>(c) Financial Data</p>
                        <p>(d) Transaction Data</p>
                        <p>(e) Marketing Data</p>
                      </td>
                      <td
                        >Necessary for our legitimate interests (to study how
                        customers use our products/services, to develop them, to
                        grow our business and to inform and assess the
                        effectiveness of our marketing strategy)</td
                      >
                    </tr>
                    <tr>
                      <td
                        >To ensure that content from our site is presented in
                        the most effective manner for you and for your computer
                        and to allow you to participate in interactive features
                        of our service</td
                      >
                      <td>
                        <p>(a) Usage Data</p>
                        <p>(b) Technical Data</p>
                      </td>
                      <td
                        >Necessary for our legitimate interests (to define types
                        of customers for our products and services, to keep our
                        website updated and relevant, to develop our business
                        and to inform our marketing strategy)</td
                      >
                    </tr>
                    <tr>
                      <td
                        >To administer our site and for internal operations,
                        including troubleshooting, data analysis, testing,
                        research, statistical and survey purposes and as part of
                        our efforts to keep our site safe and secure</td
                      >
                      <td>
                        <p>(a) Usage Data</p>
                        <p>(b) Technical Data</p>
                      </td>
                      <td>
                        <p
                          >(a) Necessary for our legitimate interests (to keep
                          our website operating and functioning well)</p
                        >
                        <p>(b) Necessary to comply with a legal obligation</p>
                      </td>
                    </tr>
                    <tr>
                      <td
                        >To train and deploy AI models for commercial and CRM
                        purposes, including predicting sales likelihood and
                        categorising company records</td
                      >
                      <td>
                        <p>(a) Technical Data</p>
                        <p>(b) Usage Data</p>
                        <p>(c) Transaction Data</p>
                        <p>(d) Non-identifiable behavioural or event signals</p>
                      </td>
                      <td>
                        <p
                          >Necessary for our legitimate interests (to enhance
                          service delivery, improve internal efficiency and
                          support sales and marketing strategies)</p
                        >
                      </td>
                    </tr>

                    <tr>
                      <td
                        >To provide you with marketing communications from other
                        companies within our group where you have consented to
                        receive such communications or where you have not
                        objected</td
                      >
                      <td>
                        <p>(a) Identity Data</p>
                        <p>(b) Contact Data</p>
                        <p>(c) Transaction Data</p>
                        <p>(d) Marketing Data</p>
                      </td>
                      <td>
                        <p>(a) Consent (where required by law)</p>
                        <p
                          >(b) Legitimate interests (where permitted, to promote
                          relevant services across our group and develop our
                          business)</p
                        >
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p style="font-weight: bold">Marketing</p>
              <p
                >We may use your Personal Data to send you marketing
                communications by email, SMS or other electronic means. This may
                include information about our own products and services, as well
                as those offered by other companies within our corporate group,
                including Rho Publishing and Consulting Ltd (trading as Rho
                Motion).
              </p>
              <p>You will only receive such communications where: </p>
              <ul>
                <li>you have provided your consent; or</li>
                <li
                  >you are an existing customer and we are permitted to contact
                  you under applicable laws, and you have not opted out.</li
                >
              </ul>
              <p
                >This may include marketing communications relating to joint
                events we may host with third-party partners, provided you have
                not objected or where your consent has been obtained as
                required.</p
              >
              <p
                >You have the right to opt out of receiving marketing
                communications at any time by using the unsubscribe link in any
                email or by contacting us at the contact details set out
                below.</p
              >
              <p style="font-weight: bold">Cookies</p>
              <p
                >Our website uses cookies to distinguish you from other users of
                our website. This helps us to provide you with a good experience
                when you browse our website and also allows us to improve our
                site.</p
              >
              <p
                >Cookies provide information regarding the computer used by a
                visitor. We may use cookies where appropriate to gather
                information about your computer in order to assist us in
                improving our website.</p
              >
              <p
                >We may gather information about your general internet use by
                using the cookie. Where used, these cookies are downloaded to
                your computer and stored on the computer’s hard drive. Such
                information will not identify you personally; it is statistical
                data which does not identify any personal details whatsoever.</p
              >
              <p
                >Our advertisers may also use cookies, over which we have no
                control. Such cookies (if used) would be downloaded once you
                click on advertisements on our website.</p
              >
              <p
                >You can adjust the settings on your computer to decline any
                cookies if you wish. This can be done within the “settings”
                section of your computer. For more information please read the
                advice at AboutCookies.org.</p
              >
            </li>
          </ol>
        </li>
        <li>
          <p style="font-weight: bold">Google Ads Enhanced Conversions</p>
          <p
            >We use Google Ads Enhanced Conversions to help measure the
            performance of our paid advertising campaigns more accurately. This
            feature uses information you voluntarily submit on our website such
            as your email address or phone number during a form submission to
            match ad interactions with subsequent actions on our site.</p
          >
          <p
            >Before any data is transmitted to Google, it is securely encrypted
            using the SHA256 hashing algorithm, converting the information into
            a non-reversible, coded format. Google uses this hashed data solely
            for aggregated and anonymised conversion reporting, and in line with
            its strict privacy standards. No personally identifiable information
            is shared with Google in a readable form. You can learn more about
            how Google handles this data
            <a
              href="https://support.google.com/google-ads/answer/9888656"
              target="_blank"
              >here</a
            >.</p
          >
        </li>
        <li>
          <p style="font-weight: bold"
            >Administrative Access and Impersonation</p
          >
          <p>
            In limited circumstances, authorised members of our internal
            commercial operations team (designated CRM Administrators) may
            access client accounts through a secure impersonation feature. This
            is used solely to verify that clients have the appropriate access to
            the products and services they have subscribed to, troubleshoot user
            experience issues, or provide customer support.
          </p>
          <p>
            While impersonating an account, CRM Administrators may view certain
            personal data associated with your account. This access is strictly
            controlled, auditable, and used only for the purposes described
            above. We do not use this functionality for any form of profiling or
            marketing, and impersonation activity is logged and monitored to
            ensure accountability and compliance with our data protection
            obligations.
          </p>
          <p>
            This practice is based on our legitimate interest in ensuring proper
            delivery of services and customer support, and does not result in
            the processing of any additional categories of personal data beyond
            what we already collect and maintain in accordance with this Notice.
          </p>
          <p style="text-decoration: underline"
            >Use of Artificial Intelligence (AI)</p
          >
          <p
            >We use artificial intelligence (AI) tools to support internal
            operations such as:</p
          >
          <ul>
            <li>Categorising company records within our CRM</li>
            <li
              >Predicting the likelihood of sales conversion based on anonymised
              behavioural and transactional signals</li
            >
          </ul>
          <p
            >These models are trained on aggregated and de-identified data. No
            decisions are made solely by automated means that produce legal or
            similarly significant effects on individuals.</p
          >
          <p
            >This processing is based on our legitimate interest in improving
            service efficiency and sales operations. Human oversight is always
            applied where decisions influence outcomes.</p
          >
        </li>
        <li>
          <p style="font-weight: bold">SHARING YOUR PERSONAL DATA</p>
          <p
            >We may share your Personal Data with the parties set out below for
            the purposes set out in the table above:</p
          >
          <ol type="a">
            <li
              >Any member of our group, which means our subsidiaries, our
              ultimate holding company and its subsidiaries, as defined in
              section 1159 of the UK Companies Act 2006. This includes Rho
              Publishing and Consulting Ltd (trading as Rho Motion). Your
              Personal Data may be shared with other group companies to enable
              them to provide you with relevant marketing communications
              (subject to your preferences and applicable law).</li
            >
            <li>Selected third parties including:</li>
            <ol type="i">
              <li
                >suppliers, sub-contractors, service providers/vendors for the
                performance of any contract we enter into with them or you (e.g.
                Personal Data may be shared with service providers that perform
                services on our behalf to carry out the uses of Personal Data
                described above in the section titled “Use of Personal Data
                About You.” This may include the provision of services such as
                payroll support, benefits, tax and travel management, employment
                eligibility verification, health and safety experts, and
                relocation companies. Most of these entities are contractually
                bound to use the Personal Data they receive only for the
                purposes of fulfilling their contractual obligations);</li
              >
              <li
                >business partners (e.g., advertisers and advertising networks)
                that require the data to select and serve relevant adverts to
                you and others. We do not disclose information about
                identifiable individuals to such third parties, but we will
                provide them with anonymized aggregate information about our
                users (for example, we may inform them that 500 men aged under
                30 have clicked on their advertisement on any given day). We may
                also use such aggregate information to help advertisers reach
                the kind of audience they want to target (for example, women in
                a particular city location). We may make use of the Personal
                Data we have collected from you to enable us to comply with our
                advertisers’ wishes by displaying their advertisement to that
                target audience;</li
              >
              <li
                >analytics and search engine providers that assist us in the
                improvement and optimization of our site.</li
              >
              <li
                >technology providers who assist us in deploying AI and
                analytics capabilities on our behalf (these providers do not
                receive personally identifiable data, only aggregated or
                anonymised datasets)</li
              >
              <li
                >professional advisors (e.g. we may disclose your Personal Data
                to our professional advisors such as our attorneys, accountants,
                financial advisors, etc.).</li
              >
            </ol>
          </ol>
          <p>
            From time to time, we may partner with third-party organisations,
            professional bodies, or institutes (for example, industry
            associations, research institutes or event partners) to host joint
            events, webinars or other collaborative activities. Where you
            register to attend such events, we may collect your Personal Data
            for event administration and communication purposes. In certain
            circumstances, and where you have consented or where permitted under
            applicable law, we may also share registration information (such as
            your name, company, role, and email address) with the co-hosting
            partner for their own use in relation to the event or for subsequent
            direct communications. Such partners will act as independent data
            controllers in respect of any Personal Data shared with them, and
            their own privacy policies will apply to their handling of your
            information. We encourage you to review the privacy policies of any
            third-party partners before providing your Personal Data. If you do
            not wish for your Personal Data to be shared in this way, please
            contact us prior to registering or via the contact details provided
            in this Privacy Notice.
          </p>
          <p
            >We may also disclose your Personal Data to third parties in the
            following circumstances:</p
          >
          <ol type="a">
            <li
              >In the event that we sell or buy any business or assets, in which
              case we will disclose your Personal Data to the prospective seller
              or buyer of such business or assets.</li
            >
            <li
              >If Benchmark or substantially all of its assets are acquired by a
              third party, in which case Personal Data held by it about its
              customers will be one of the transferred assets.</li
            >
            <li
              >If we are under a duty to disclose or share your Personal Data in
              order to comply with any legal obligation, or in order to enforce
              or apply our terms of use (<a
                href="https://www.benchmarkminerals.com/terms"
                >https://www.benchmarkminerals.com/terms/</a
              >) and other agreements; or to protect the rights, property, or
              safety of Benchmark, our customers, or others. This includes
              exchanging information with other companies and organizations for
              the purposes of fraud protection and credit risk reduction.</li
            >
          </ol>
        </li>
        <li>
          <p style="font-weight: bold">TRANSFERS OUTSIDE THE UK</p>
          <p
            >In general, when transferring your Personal Data outside the UK:</p
          >
          <ol type="a">
            <li
              >We will only transfer your Personal Data to countries that have
              been deemed to provide an adequate level of protection for
              Personal Data.</li
            >
            <li
              >Where we use certain service providers, we may use specific
              contracts approved for use in the UK which give Personal Data the
              same protection it has in the UK.</li
            >
          </ol>
          <p
            >Please contact us if you want further information on the specific
            mechanism used by us when transferring your Personal Data out of the
            UK.</p
          >
        </li>
        <li>
          <p style="font-weight: bold">STORAGE OF YOUR PERSONAL DATA</p>
          <p
            >We have implemented administrative, technical, and physical
            safeguards intended to protect the Personal Data you provide to us.
            Unfortunately, the transmission and storage of information via the
            internet is not completely secure. Although we will do our best to
            protect your Personal Data, we cannot guarantee the security of your
            data transmitted to Our Site; any transmission is at your own
            risk.</p
          >
        </li>
        <li>
          <p style="font-weight: bold">DATA RETENTION</p>
          <p
            >We retain Personal Data we collect from you only for as long as it
            is needed for the purposes described above in “We use information
            held about you in the following ways,” or as otherwise required or
            permitted by law.</p
          >
          <p
            >When we have no ongoing legitimate business need or purpose to
            process your Personal Data, we will either delete or anonymize it,
            or, if this is not possible (for example, because your Personal Data
            has been stored in backup archives), then we will securely store
            your Personal Data and isolate it from any further processing until
            deletion is possible.</p
          >
        </li>
        <li>
          <p style="font-weight: bold">GROUP MARKETING PREFERENCES</p>
          <p
            >You may be invited to express your marketing preferences separately
            for Benchmark Mineral Intelligence and Rho Motion. You can change
            your preferences or opt out of marketing from either or both
            entities at any time.</p
          >
        </li>
        <li>
          <p style="font-weight: bold">YOUR RIGHTS</p>
          <p
            >If you are an individual covered by this Privacy Notice, under
            certain circumstances, and subject to which laws apply, you may have
            the following rights under data protection laws in relation to your
            Personal Data:</p
          >
          <ul>
            <li
              ><span style="font-weight: bold">ACCESS:</span> The right to
              request access to the Personal Data we hold about you. This
              enables you to receive a copy of the Personal Data we hold about
              you and to check that we are processing it lawfully.</li
            >
            <li
              ><span style="font-weight: bold">RECTIFICATION:</span> The right
              to request correction of the Personal Data we hold about you. This
              enables you to have any incomplete or inaccurate data we hold
              about you corrected, though we may need to verify the accuracy of
              any new data you provide to us.</li
            >
            <li
              ><span style="font-weight: bold">ERASURE:</span> The right to
              request we erase your Personal Data in certain circumstances. This
              enables you to ask us to delete or remove Personal Data where
              there is no good reason for us to continue to process it. You also
              have the right to ask us to delete or remove your Personal Data
              where you have successfully exercised your right to object to
              processing (see below), where we may have processed your
              information unlawfully or where we are required to erase your
              Personal Data to comply with local law. Note, however, that we may
              not always be able to comply with your request of erasure for
              specific legal reasons which will be notified to you, if
              applicable, at the time of your request.</li
            >
            <li
              ><span style="font-weight: bold">RESTRICTION OF PROCESSING:</span>
              In certain circumstances you can request that we suspend the
              processing of your Personal Data.</li
            >
            <li
              ><span style="font-weight: bold">OBJECT:</span> The right to ask
              us not to process your Personal Data in certain circumstances.
              Specifically, where we are relying on a legitimate interest (or
              those of a third party) and there is something about your
              particular situation which makes you want to object to processing
              on this ground as you feel it impacts on your fundamental rights
              and freedoms. You also have the right to object where we are
              processing your Personal Data for direct marketing purposes. In
              some cases, we may demonstrate that we have compelling legitimate
              grounds to process your information which override your rights and
              freedoms. You may also object to any processing involving
              profiling or automated decision-making, including any AI-supported
              analysis, where this affects your rights or freedoms.</li
            >
            <li
              ><span style="font-weight: bold">PORTABILITY:</span> In certain
              circumstances, you can request the transfer of your Personal Data
              to you or a third party. We will provide to you, or a third party
              you have chosen, your Personal Data in a structured, commonly
              used, machine-readable format.</li
            >
          </ul>
          <p
            >Where we are relying on consent to process your Personal Data you
            have the right to withdraw your consent. However, this will not
            affect the lawfulness of any processing carried out before you
            withdraw your consent. If you withdraw your consent, we may not be
            able to provide certain products or services to you. We will advise
            you if this is the case at the time you withdraw your consent.</p
          >
          <p
            >You are not required to pay any charge for exercising your rights.
            However, we may charge a reasonable fee if your request is clearly
            unfounded, repetitive or excessive. Alternatively, we could refuse
            to comply with your request in these circumstances.</p
          >
          <p
            >If you wish to exercise any of the rights set out above, please
            contact us at the details in section 1(c) above. We try to respond
            to all legitimate requests within one month. Occasionally it could
            take us longer than a month if your request is particularly complex
            or you have made a number of requests. In this case, we will notify
            you and keep you updated.</p
          >
        </li>
        <li>
          <p style="font-weight: bold">HOW TO COMPLAIN</p>
          <p
            >Please let us know if you are unhappy with how we use your Personal
            Data. You can contact us at the details above. You have the right to
            complain to the regulator and to lodge an appeal if you are not
            happy with the outcome of a complaint. In the UK, the regulator is
            the Information Commissioner's Office. Details of how to make a
            complaint can be found on
            <a href="https://ico.org.uk/make-a-complaint/">their website</a>.</p
          >
        </li>
        <li>
          <p style="font-weight: bold"
            >ADDITIONAL PRIVACY NOTICE FOR CALIFORNIA RESIDENTS</p
          >
          <p
            >Individuals who are residents of California have certain additional
            rights under the California Consumer Privacy Act, as amended by the
            California Privacy Rights Act and accompanying regulations
            (collectively, the “CCPA”).</p
          >
          <p
            >This Privacy Notice describes the Personal Data we collect or
            process about individuals who are California residents, as well as
            how we use, disclose, and protect that Personal Data. This Privacy
            Notice also outlines the rights you have with respect to Personal
            Data that we collect or process.</p
          >
          <ol type="a">
            <li>
              <p style="font-weight: bold">Supplemental Disclosures:</p>

              <p
                >In the past 12 months, we have collected Personal Data
                discussed above in “The Types of Personal Data we Process.” The
                chart below sets forth the categories of third parties to which
                we disclosed Personal Data for our operational business purposes
                within the preceding 12 months and the purposes for such
                disclosures.</p
              >
              <table>
                <thead>
                  <tr>
                    <th>Category of Personal Data</th>
                    <th
                      >Categories of Third Parties to whom the information is
                      disclosed for Operational Business Purposes</th
                    >
                    <th>Business Purpose for Disclosure</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td
                      >Personal Identifiers: Contact details (including your
                      name, billing address, delivery address, e-mail address
                      and phone number); identification details (including your
                      name personal description and photograph, date of birth,
                      gender, marital status, title); and Financial details
                      (including account and payment card details).</td
                    >
                    <td
                      >Affiliates; service providers/vendors; professional
                      advisors; third parties in connection with contractual
                      obligations.</td
                    >
                    <td>To provide Our Site and Services.</td>
                  </tr>
                  <tr>
                    <td
                      >Internet or other network activity information:
                      Information regarding your interaction with Our Site and
                      other applications such as analytics information; log
                      data; cookies; links you use or web pages you visit; other
                      applications; browser type; internet service provider
                      (ISP); cookies; and mobile device information including
                      device identifier or other information.</td
                    >
                    <td
                      >Affiliates; vendors; professional advisors; legal
                      authorities, government agencies, and regulators.</td
                    >
                    <td
                      >To ensure compliance with company policies; to create and
                      maintain cybersecurity controls; to ensure compliance with
                      legal obligations.</td
                    >
                  </tr>
                </tbody>
              </table>
            </li>
            <li>
              <p style="font-weight: bold">Sensitive Personal Data</p>
              <p
                >Please note that we do not collect Sensitive Personal Data when
                you interact with Our Site or use our Services.</p
              >
            </li>

            <li>
              <p style="font-weight: bold">Selling and Sharing Personal Data</p>
              <p
                >Please note that in the past 12 months, we have not sold or
                shared the Personal Data of any individuals as such terms are
                defined by the CCPA.</p
              >
            </li>

            <li>
              <p style="font-weight: bold">Opt-Out Preference Signals</p>
              <p
                >In addition to the privacy controls described above, we
                recognize Global Privacy Control signals where legally required.
                A Global Privacy Control (“GPC”) is a browser setting that a
                user can set in order to send a signal to each website visited
                regarding the user’s privacy preferences, such as not to share
                or sell user’s Personal Data. If your browser or browser
                extension has GPC enabled, our websites will automatically
                recognize that signal and opt you out of the sale of your
                Personal Data. For more information about GPC and how to
                implement GPC opt-out preference signals, please visit Global
                Privacy Control.</p
              >
            </li>

            <li>
              <p style="font-weight: bold">Your CCPA Rights</p>
              <p
                >Under the CCPA, individuals who are California residents have
                specific rights regarding their Personal Data. These rights are
                subject to certain exceptions. When required, we will respond to
                most requests within 45 days unless it is reasonably necessary
                to extend the response time.</p
              >

              <p>
                <span style="font-weight: bold">
                  Right to Disclosure of Information:
                </span>
                You have the right to request that we disclose certain
                information regarding our practices with respect to Personal
                Data. If you submit a valid and verifiable request and we
                confirm your identity and/or authority to make the request, we
                will disclose to you any of the following at your direction:
              </p>

              <ul>
                <li>
                  The categories of Personal Data we have collected about you in
                  the last 12 months.
                </li>
                <li>
                  The categories of sources for the Personal Data we have
                  collected about you in the last 12 months.
                </li>
                <li>
                  Our business or commercial purpose for collecting that
                  Personal Data.
                </li>
                <li>
                  The categories of third parties with whom we share that
                  Personal Data.
                </li>
                <li>
                  The specific pieces of Personal Data we collected about you.
                </li>
              </ul>

              <p>
                <span style="font-weight: bold">
                  Right to Delete Personal Data:
                </span>
                You have the right to request that we delete any of your
                Personal Data collected from you and retained, subject to
                certain exceptions. Upon receiving a verified request to delete
                your Personal Data, we will do so unless otherwise authorized by
                applicable law or regulations, or it is necessary to retain the
                Personal Data to protect our rights or the rights of others.
              </p>

              <p>
                <span style="font-weight: bold">
                  Right to Correct your Personal Data:
                </span>
                If you find that we maintain any inaccurate Personal Data, you
                have the right to request that we correct such inaccuracy.
              </p>

              <p>
                <span style="font-weight: bold">
                  Right to Opt-Out of the Sale or Sharing of Your Personal Data:
                </span>
                You have the right to opt-out of the sale or sharing of your
                Personal Data as such terms are defined by the CCPA. However, as
                we neither share nor sell your Personal Data, we have not
                provided you with a link to exercise these rights.
              </p>

              <p>
                <span style="font-weight: bold">
                  Right to Non-Discrimination:
                </span>
                You have the right not to be discriminated against for the
                exercise of your California privacy rights described above.
              </p>
            </li>
            <li>
              <p style="font-weight: bold">How to Exercise these Rights</p>
              <p>
                If you wish to submit a request to exercise one or more of the
                rights listed above you can do this by emailing us at
                <a href="mailto:amiller@benchmarkminerals.com"
                  >amiller@benchmarkminerals.com</a
                >, using our webform, or by mail to:
                <a href="mailto:amiller@benchmarkminerals.com"
                  >amiller@benchmarkminerals.com</a
                >.
              </p>
              <p>
                Please include your question or concerns in your note to us. We
                will respond to verifiable requests for disclosure or deletion
                of Personal Data free of charge, within 45 days of receipt.
              </p>
              <p>
                In order to protect your privacy and the security of your
                information, we verify consumer requests where legally permitted
                by requesting identification documents and other documentation
                necessary to confirm your identity. Any additional information
                you provide will be used only to verify your identity and not
                for any other purpose.
              </p>
            </li>

            <li>
              <p style="font-weight: bold">Authorized Agents</p>
              <p>
                If you want to make a request as an authorized agent on behalf
                of a California resident, you may use the submission methods
                noted above. As part of our verification process, we may request
                that you provide, as applicable, proof concerning your status as
                an authorized agent. In addition, we may require the individual
                on whose behalf you are making the request to verify their own
                identity or your permission to submit the request.
              </p>
            </li>
          </ol>

          <!-- Revision -->
        </li>

        <li>
          <p style="font-weight: bold">
            SPECIAL NOTICE TO COLORADO, VIRGINIA, UTAH, OREGON, MONTANA, TEXAS,
            TENNESSEE, FLORIDA AND CONNECTICUT RESIDENTS
          </p>
          <p
            >This section describes the Personal Information we collect or
            process about Colorado, Virginia, Utah, Oregon, Montana, Texas,
            Florida, and Connecticut residents and describes your rights
            pursuant to the Colorado Privacy Act, Virginia Consumer Data
            Protection Act, the Utah Consumer Privacy Act, Oregon Consumer
            Privacy Act, Montana Consumer Data Privacy Act, Tennessee
            Information Protection Act, Texas Data Privacy and Security Act,
            Florida Digital Bill of Rights and Connecticut Data Privacy Act.</p
          >
          <ol type="a">
            <li>
              <p style="font-weight: bold">Your Rights</p>
              <p
                >Users who are residents of these states have specific rights
                regarding their Personal Data. These rights are subject to
                certain exceptions described below. When required, we will
                respond to most requests within 45 days unless it is reasonably
                necessary to extend the response time. Subject to certain
                limitations, you have the following rights:</p
              >
              <ol type="i">
                <li>
                  <span style="font-weight: bold"
                    >Right of Access & Confirmation:</span
                  >
                  You have the right to confirm whether we process your Personal
                  Data and request access to such Personal Data.
                </li>
                <li>
                  <span style="font-weight: bold"> Right to Deletion: </span>
                  You have the right to request that we delete the Personal Data
                  we have collected about you.
                </li>
                <li>
                  <span style="font-weight: bold">
                    Right to Data Portability:
                  </span>
                  You have the right to request that we provide you with a copy
                  of your Personal Data in a portable format.
                </li>
              </ol>
              <p>
                <span style="font-weight: bold"> Opt-Outs: </span>
                At this time, we do not process your Personal Data for purposes
                of targeted advertising, the sale of Personal Data, or profiling
                in furtherance of decisions that produce legally or similarly
                significant effects concerning a consumer, and therefore, such
                opt-out rights are not available.
              </p>

              <p style="font-weight: bold">
                Colorado, Connecticut, Oregon, Montana, Texas, Florida, and
                Virginia residents only:
              </p>
              <ol type="i">
                <li>
                  <span style="font-weight: bold"> Right to Correction: </span>
                  You have the right to correct inaccuracies in your Personal
                  Data.
                </li>
              </ol>
              <p style="font-weight: bold"> Oregon residents only: </p>
              <ol type="i">
                <li>
                  <span style="font-weight: bold">
                    The Right to a List of Third-Party Disclosures:
                  </span>
                  At our election, a list of specific third parties, other than
                  natural persons, to which we have disclosed: (a) the
                  consumer’s Personal Data; or (b) any Personal Data.
                </li>
              </ol>
              <p style="font-weight: bold"> Florida residents only: </p>
              <ol type="i">
                <li>
                  <span style="font-weight: bold">
                    The Right to Opt-Out of The Collection or Processing of
                    Sensitive Data
                  </span>
                </li>
                <li>
                  <span style="font-weight: bold">
                    The Right to Opt-Out of the Collection of Personal Data
                    Collected Through the Operation of a Voice Recognition or
                    Facial Recognition Feature:
                  </span>
                  Please note that we do not collect any personal information in
                  this manner.
                </li>
              </ol>
            </li>

            <li>
              <p style="font-weight: bold">How to Exercise these Rights</p>
              <p
                >You may submit a verifiable consumer request to us for
                disclosure or deletion of Personal Data by contacting us at
                <a href="mailto:amiller@benchmarkminerals.com"
                  >amiller@benchmarkminerals.com</a
                >.</p
              >
              <p
                >Alternatively, you may call us at our toll-free number
                <a href="tel:+18449910475">+18449910475</a>.</p
              >
              <p
                >We will respond to verifiable requests for disclosure or
                deletion of Personal Data free of charge, within 10 days of
                receipt.</p
              >
              <p
                >In the event that you are unhappy with the outcome of your
                request to exercise one of your rights above, you may appeal our
                decision by emailing us at
                <a href="mailto:amiller@benchmarkminerals.com"
                  >amiller@benchmarkminerals.com</a
                >. If additional information is needed for your appeal, we may
                request you to provide additional information to the extent
                legally permitted.</p
              >
              <p
                >Depending on your state of residence, you may also have the
                right to designate an agent to exercise these rights on your
                behalf. We may require proof that you have designated the
                authorized agent to act on your behalf and to verify your
                identity directly with us. Please contact us at
                <a href="mailto:amiller@benchmarkminerals.com"
                  >amiller@benchmarkminerals.com</a
                >
                for more information if you wish to submit a request through an
                authorized agent</p
              >
            </li>
          </ol>
        </li>
      </ol>

      <p>
        Swindon Head Office -
        <a href="mailto:office@benchmarkminerals.com">
          office@benchmarkminerals.com
        </a>
        or call Emily Dunn on <a href="tel:+447833464844">+44 7833 464844</a>
      </p>

      <p>August 2024.</p>
    </div>
  </SimplePageContent>
</template>
